/* Cart shares Account's header popout presentation. Prestige retains native
 * cart operations and dialog lifecycle; Search uses its own foreground modal. */
import { announceStatus } from 'theme';
import { animate } from 'vendor';
import { animatePopout, preparePopout, releasePopout } from 'nwp-header';

const initialized = new Map();
const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
const HOVER_TOLERANCE = 250;
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');

function init(panel, { kind, triggerSelector, widthRem, deferred = false }) {
  if (initialized.has(panel) || !panel.shadowRoot) return;
  if (panel.open) {
    // Finish any native modal opened before this module loaded. Replacing its
    // focus/scroll seams mid-session would strand the native scroll lock.
    panel.addEventListener('dialog:after-hide', () => init(panel, { kind, triggerSelector, widthRem, deferred: true }), { once: true });
    return;
  }
  const isTrigger = (node) => node?.closest?.(triggerSelector)?.getAttribute('aria-controls') === panel.id;
  const findTrigger = () => Array.from(document.querySelectorAll(triggerSelector))
    .find((element) => element.getAttribute('aria-controls') === panel.id && element.getBoundingClientRect().width > 0);
  if (!findTrigger()) return; // Cart-page setting and no-header layouts keep Prestige's fallback.

  const cleanup = new AbortController();
  const { signal } = cleanup;
  const owner = panel.closest('.shopify-section');
  let hoverArmed = !deferred;
  let observedHeader;
  let updateTimer;
  let trigger;
  let bridge;
  let dismissTimer;
  let frame;
  let openingOnHover = false;
  let pinned = false;
  let closing = false;
  let focusOnOpen = false;
  let returnFocus = false;
  let updating = false;
  let focusedControl;
  let invokingControl;
  const noteDialogs = new WeakSet();

  function prepareNotes() {
    if (kind !== 'cart') return;
    panel.querySelectorAll('cart-note-dialog').forEach((note) => {
      if (noteDialogs.has(note)) return;
      noteDialogs.add(note);
      note.setAttribute('initial-focus', 'textarea');
      // The editor participates in the compact footer's layout. Preserve its
      // native saving/focus lifecycle while replacing the full-drawer slide.
      Object.defineProperties(note, {
        allowOutsideClickForTarget: { configurable: true, value: (target) => target?.closest?.('[aria-controls]')?.getAttribute('aria-controls') === note.id },
        createEnterAnimationControls: { configurable: true, value: () => animate(note, { opacity: [0, 1] }, { duration: reduceMotion.matches ? 0 : 0.16 }) },
        createLeaveAnimationControls: { configurable: true, value: () => animate(note, { opacity: [1, 0] }, { duration: reduceMotion.matches ? 0 : 0.12 }) },
      });
    });
  }

  // These public DialogElement seams are also used by Prestige's own PopIn.
  // Hover must not steal focus, lock scrolling, or relocate the section host.
  Object.defineProperties(panel, {
    shouldTrapFocus: { configurable: true, get: () => false },
    shouldLock: { configurable: true, get: () => false },
    shouldAppendToBody: { configurable: true, get: () => false },
    createEnterAnimationControls: { configurable: true, value: () => animatePopout(panel, true) },
    createLeaveAnimationControls: { configurable: true, value: () => animatePopout(panel, false) },
  });
  panel.classList.add('nwp-popout', 'nwp-header-popout');
  panel.setAttribute('aria-modal', 'false');
  panel.tabIndex = -1;

  const cancelDismiss = () => clearTimeout(dismissTimer);
  const contains = (node) => node instanceof Node && (panel.contains(node) || panel.shadowRoot.contains(node));
  const engaged = () => contains(document.activeElement) || updating;
  const firstControl = () => panel.querySelector('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"])') ?? panel;

  function position() {
    trigger = findTrigger();
    if (!trigger) return;
    const header = trigger.closest('.header');
    if (!header) return;
    if (observedHeader !== header) {
      headerObserver.disconnect();
      headerObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
      observedHeader = header;
    }
    if (header.classList.contains('is-hidden') && panel.open) { close(); return; }
    const styles = getComputedStyle(header);
    // Geometry and motion follow the header; the dark panel owns its paint.
    for (const token of ['--nwp-ease', '--nwp-popout-gap', '--nwp-popout-notch']) {
      const value = styles.getPropertyValue(token).trim();
      if (value) panel.style.setProperty(token, value);
    }
    const rootSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const tokenPx = (name, fallback) => {
      const token = styles.getPropertyValue(name).trim();
      const value = parseFloat(token);
      return Number.isFinite(value) ? value * (token.endsWith('rem') ? rootSize : 1) : fallback;
    };
    const viewport = window.visualViewport;
    const viewTop = viewport?.offsetTop ?? 0;
    const viewHeight = viewport?.height ?? innerHeight;
    const viewWidth = document.documentElement.clientWidth;
    const gutter = viewWidth < 700 ? 12 : 24;
    const triggerRect = trigger.getBoundingClientRect();
    const preferredWidth = panel.classList.contains('drawer--center-body')
      ? tokenPx('--nwp-utility-panel-width', 23.5 * rootSize)
      : widthRem * rootSize;
    const width = Math.min(preferredWidth, viewWidth - gutter * 2);
    const anchorLeft = styles.direction === 'rtl' ? triggerRect.left : triggerRect.right - width;
    const left = Math.max(gutter, Math.min(anchorLeft, viewWidth - gutter - width));
    const top = Math.max(viewTop + 8, header.getBoundingClientRect().bottom
      + tokenPx('--nwp-popout-gap', 12) - tokenPx('--nwp-popout-notch', 8.5));
    const launcher = document.getElementById('NwpChatLaunchpoint');
    const chatTop = launcher?.getBoundingClientRect().top;
    const bottom = Number.isFinite(chatTop) ? Math.min(viewTop + viewHeight - gutter, chatTop - 12) : viewTop + viewHeight - gutter;
    const available = Math.max(0, bottom - top);
    panel.toggleAttribute('data-nwp-compact', available < 440);
    panel.style.setProperty('--nwp-header-popout-left', `${left}px`);
    panel.style.setProperty('--nwp-header-popout-top', `${top}px`);
    panel.style.setProperty('--nwp-header-popout-width', `${width}px`);
    panel.style.setProperty('--nwp-header-popout-height', `${available}px`);
    trigger.setAttribute('aria-haspopup', 'dialog');
    if (!bridge?.isConnected || bridge.parentElement !== trigger.parentElement) {
      bridge?.remove();
      bridge = document.createElement('span');
      bridge.className = 'nwp-header-popout__bridge';
      bridge.setAttribute('aria-hidden', 'true');
      trigger.parentElement.append(bridge);
    }
    const parentRect = trigger.parentElement.getBoundingClientRect();
    bridge.style.top = `${triggerRect.bottom - parentRect.top}px`;
    bridge.style.height = `${Math.max(0, top - triggerRect.bottom + tokenPx('--nwp-popout-notch', 8.5))}px`;
    preparePopout(panel, trigger);
  }

  function schedulePosition() {
    if (!panel.open || frame) return;
    frame = requestAnimationFrame(() => { frame = null; position(); });
  }

  function open({ hover = false, keyboard = false } = {}) {
    cancelDismiss();
    focusOnOpen ||= keyboard;
    if (!hover) pinned = true;
    if (closing) {
      returnFocus = false;
      closing = false;
    }
    if (panel.open) {
      if (keyboard) { firstControl()?.focus({ preventScroll: true }); focusOnOpen = false; }
      return;
    }
    openingOnHover = hover;
    panel.show();
    openingOnHover = false;
  }

  function close({ restore = false } = {}) {
    cancelDismiss();
    focusOnOpen = false;
    returnFocus ||= restore;
    if (panel.open) panel.hide();
  }

  const scheduleDismiss = () => {
    cancelDismiss();
    if (!pinned && !engaged()) dismissTimer = setTimeout(() => {
      if (!pinned && !engaged()) close();
    }, HOVER_TOLERANCE);
  };

  panel.addEventListener('dialog:before-show', (event) => {
    if (event.target !== panel) return;
    const active = document.activeElement;
    if (!openingOnHover && active instanceof HTMLElement && active !== document.body && !contains(active)) invokingControl = active;
    pinned = !openingOnHover;
    closing = false;
    panel.setAttribute('data-nwp-popout-visible', '');
    position();
    trigger?.parentElement.classList.add('nwp-header-popout-open');
    document.querySelectorAll('.nwp-header-popout[open]').forEach(other => { if (other !== panel) other.hide(); });
    document.querySelectorAll('.header details[aria-expanded="true"]').forEach((details) => {
      details.parentElement.toggle?.(false);
    });
  }, { signal });
  panel.addEventListener('dialog:after-show', (event) => {
    if (event.target !== panel) return;
    if (panel.open && !closing && pinned && !contains(document.activeElement)) {
      (focusOnOpen ? firstControl() : panel).focus({ preventScroll: true });
    }
    focusOnOpen = false;
  }, { signal });
  panel.addEventListener('dialog:before-hide', (event) => {
    if (event.target !== panel) return;
    closing = true;
    panel.querySelectorAll('cart-note-dialog[open]').forEach((note) => {
      // A nested trap must not survive its parent or return focus into a
      // hidden panel when the shopper switches to another header control.
      note.focusTrap.deactivate({ returnFocus: false });
      note.hide();
    });
  }, { signal });
  panel.addEventListener('dialog:force-close', (event) => {
    if (event.target === panel) returnFocus = true;
  }, { capture: true, signal });
  panel.addEventListener('dialog:after-hide', (event) => {
    if (event.target !== panel) return;
    closing = false;
    // A native Add to Cart may reopen during the outgoing animation. Do not
    // let that earlier completion hide a newly opened, populated panel.
    if (panel.open) { panel.style.display = 'block'; position(); return; }
    panel.removeAttribute('data-nwp-popout-visible');
    trigger?.parentElement.classList.remove('nwp-header-popout-open');
    if (returnFocus) {
      const target = invokingControl?.isConnected && invokingControl.getClientRects().length ? invokingControl : trigger;
      target?.focus({ preventScroll: true });
    }
    invokingControl = null;
    returnFocus = false;
  }, { signal });

  document.addEventListener('pointermove', event => {
    if (event.isTrusted) hoverArmed = true;
  }, { passive: true, signal });
  document.addEventListener('pointerover', (event) => {
    if (!hoverArmed || !finePointer.matches || event.pointerType === 'touch') return;
    if (findTrigger()?.closest('x-header')?.dataset.menuOpenTrigger === 'click') return;
    if (isTrigger(event.target)) open({ hover: true });
    else if (contains(event.target) || event.target === bridge) cancelDismiss();
  }, { signal });
  document.addEventListener('pointerout', (event) => {
    if (!finePointer.matches || !panel.open) return;
    const fromPanel = contains(event.target) || isTrigger(event.target) || event.target === bridge;
    const toPanel = contains(event.relatedTarget) || isTrigger(event.relatedTarget) || event.relatedTarget === bridge;
    if (fromPanel && !toPanel) {
      scheduleDismiss();
    }
  }, { signal });

  document.addEventListener('pointerdown', (event) => {
    if (!panel.open || event.composedPath().includes(panel) || (isTrigger(event.target) || event.target === bridge)) return;
    close();
  }, { capture: true, signal });
  document.addEventListener('click', (event) => {
    if (!isTrigger(event.target)) return;
    focusOnOpen ||= event.detail === 0;
    if (closing || (panel.open && !pinned)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      open({ keyboard: focusOnOpen });
    }
    pinned = true;
  }, { capture: true, signal });
  document.addEventListener('keydown', (event) => {
    if (event.defaultPrevented) return;
    const onTrigger = isTrigger(event.target);
    if (onTrigger && ['Enter', 'ArrowDown', ' '].includes(event.key)) {
      event.preventDefault();
      open({ keyboard: true });
    } else if (onTrigger && event.key === 'Tab' && !event.shiftKey && panel.open) {
      event.preventDefault();
      open({ keyboard: true });
    } else if (event.key === 'Tab' && event.target === panel && !panel.shadowRoot.activeElement && panel.open) {
      // WebKit does not consistently advance from a tabindex=-1 shadow host
      // into its slotted links. Establish the first keyboard stop explicitly.
      event.preventDefault();
      if (event.shiftKey) close({ restore: true });
      else firstControl()?.focus();
    } else if (event.key === 'Escape' && panel.open) {
      // Let the panel's native order-note dialog handle its own Escape first.
      const note = panel.querySelector('cart-note-dialog[open]');
      if (note) { event.preventDefault(); note.hide(); return; }
      event.preventDefault();
      close({ restore: pinned || contains(document.activeElement) });
    } else if (event.key === 'Tab' && event.shiftKey) {
      if (event.target === firstControl()) {
        event.preventDefault();
        close({ restore: true });
      }
    }
  }, { signal });
  document.addEventListener('focusin', (event) => {
    if (!panel.open || updating || event.composedPath().includes(panel) || event.target === trigger) return;
    close();
  }, { signal });
  document.addEventListener('toggle', (event) => {
    if (panel.open && event.target.matches?.('.header details[aria-expanded="true"]')) close();
  }, { capture: true, signal });
  document.addEventListener('dialog:before-show', (event) => {
    if (panel.open && event.target !== panel && !contains(event.target)) close();
  }, { capture: true, signal });

  const settleUpdate = () => { updating = false; clearTimeout(updateTimer); };
  if (kind === 'cart') document.addEventListener('cart:change', event => {
    settleUpdate();
    const count = event.detail?.cart?.item_count;
    if (event.detail?.baseEvent === 'line-item:change' && Number.isSafeInteger(count) && panel.dataset.cartUpdated && document.getElementById('status-announcement')) {
      announceStatus(panel.dataset.cartUpdated.replace('[count]', String(count)));
    }
    const sectionId = owner?.id.replace(/^shopify-section-/, '');
    updating = typeof event.detail?.cart?.sections?.[sectionId] === 'string';
    if (updating) updateTimer = setTimeout(() => { settleUpdate(); scheduleDismiss(); }, 10000);
    const active = document.activeElement;
    if (panel.contains(active)) {
      const controls = Array.from(panel.querySelectorAll('line-item-quantity a, line-item-quantity input, line-item-quantity button'));
      focusedControl = controls.indexOf(active);
    }
  }, { signal });
  if (kind === 'cart') panel.addEventListener('cart-drawer:refreshed', () => {
    settleUpdate();
    prepareNotes();
    if (panel.open && focusedControl >= 0) {
      const controls = panel.querySelectorAll('line-item-quantity a, line-item-quantity input, line-item-quantity button');
      (controls[focusedControl] ?? firstControl())?.focus({ preventScroll: true });
    }
    focusedControl = undefined;
    schedulePosition();
  }, { signal });

  panel.addEventListener('cart-drawer:refresh-error', settleUpdate, { signal });
  const headerObserver = new MutationObserver(() => {
    if (observedHeader?.classList.contains('is-hidden')) close();
    else schedulePosition();
  });
  window.addEventListener('resize', schedulePosition, { passive: true, signal });
  window.addEventListener('scroll', schedulePosition, { passive: true, signal });
  window.visualViewport?.addEventListener('resize', schedulePosition, { passive: true, signal });
  window.visualViewport?.addEventListener('scroll', schedulePosition, { passive: true, signal });
  initialized.set(panel, { owner, refresh: position, dispose: () => {
    cleanup.abort();
    headerObserver.disconnect();
    settleUpdate();
    cancelDismiss();
    cancelAnimationFrame(frame);
    bridge?.remove();
    releasePopout(panel);
    panel.removeAttribute('data-nwp-popout-visible');
    trigger?.parentElement.classList.remove('nwp-header-popout-open');
    initialized.delete(panel);
  } });
  prepareNotes();
  position();
  trigger?.setAttribute('aria-expanded', 'false');
}

const initAll = () => {
  document.querySelectorAll('cart-drawer.nwp-cart-drawer').forEach(panel => init(panel, { kind: 'cart', triggerSelector: '.header__cart-link [aria-controls]', widthRem: 26.5 }));
};
await customElements.whenDefined('cart-drawer');
initAll();
document.addEventListener('shopify:section:load', () => {
  initAll();
  for (const entry of initialized.values()) entry.refresh();
});
document.addEventListener('shopify:section:unload', (event) => {
  for (const [panel, entry] of initialized) {
    if (entry.owner === event.target || !panel.isConnected || event.target.contains(panel)) entry.dispose();
  }
});
